import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Badge } from './ui/badge';
import api from '../lib/api';

const BrandManagement = ({ user }) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [editBrandForm, setEditBrandForm] = useState({ name: '', description: '' });
  
  const [brandForm, setBrandForm] = useState({
    name: '',
    description: ''
  });

  const predefinedBrands = [
    { name: 'Elegance Collection', description: 'Premium ethnic wear with contemporary designs' },
    { name: 'Royal Traditions', description: 'Traditional Indian garments with royal touch' },
    { name: 'Modern Ethnics', description: 'Fusion wear blending modern and ethnic styles' },
    { name: 'Classic Styles', description: 'Timeless ethnic designs for all occasions' },
    { name: 'Festive Couture', description: 'Special occasion and festive wear collection' }
  ];

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await api.get('/brands');
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
      toast.error('Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBrandForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await api.post('/brands', brandForm);
      
      toast.success('Brand created successfully!');
      setIsDialogOpen(false);
      resetForm();
      fetchBrands();
    } catch (error) {
      console.error('Error creating brand:', error);
      const message = error.response?.data?.detail || 'Failed to create brand';
      toast.error(message);
    }
  };

  const resetForm = () => {
    setBrandForm({
      name: '',
      description: ''
    });
  };

  const addPredefinedBrand = async (brand) => {
    try {
      await api.post('/brands', brand);
      toast.success(`${brand.name} brand added successfully!`);
      fetchBrands();
    } catch (error) {
      console.error('Error adding brand:', error);
      const message = error.response?.data?.detail || 'Failed to add brand';
      toast.error(message);
    }
  };

  const deleteBrand = async (brandId, brandName) => {
    try {
      await api.delete(`/brands/${brandId}`);
      toast.success(`${brandName} brand deleted successfully!`);
      fetchBrands();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete brand';
      toast.error(message);
    }
  };

  const startEditBrand = (brand) => {
    setEditingBrand(brand);
    setEditBrandForm({ name: brand.name || '', description: brand.description || '' });
  };

  const saveEditBrand = async () => {
    if (!editingBrand) return;
    try {
      await api.put(`/brands/${editingBrand.id}`, editBrandForm);
      toast.success(`Brand updated to "${editBrandForm.name}"!`);
      setEditingBrand(null);
      fetchBrands();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update brand'); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}-${mm}-${yy}`;
  };

  const getAvailablePredefinedBrands = () => {
    const existingNames = brands.map(brand => brand.name.toLowerCase());
    return predefinedBrands.filter(
      brand => !existingNames.includes(brand.name.toLowerCase())
    );
  };

  const canManageBrands = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="brand-management-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>
            🏷️ Brand Management
          </h1>
          <p className="text-gray-600 mt-2">Manage your garment brands and collections</p>
        </div>
        
        {canManageBrands && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
                data-testid="add-brand-button"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Brand
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-lg z-50">
              <DialogHeader>
                <DialogTitle>Add New Brand</DialogTitle>
                <DialogDescription>
                  Create a new brand for your product catalog
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Brand Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={brandForm.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Elegance Collection"
                    required
                    data-testid="brand-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={brandForm.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the brand..."
                    data-testid="brand-description-input"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <Button type="submit" className="flex-1" data-testid="submit-brand-button">
                    Create Brand
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>


      {/* Brands List */}
      <Card data-testid="brands-list">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>🏷️</span> Brands ({brands.length})
          </CardTitle>
          <CardDescription>
            Your brand portfolio and collections
          </CardDescription>
        </CardHeader>
        <CardContent>
          {brands.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏷️</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Brands Yet</h3>
              <p className="text-gray-600 mb-6">Start building your brand portfolio by adding your first brand.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {canManageBrands && (
                  <>
                    <Button 
                      onClick={() => setIsDialogOpen(true)}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600"
                    >
                      Create Custom Brand
                    </Button>
                    {predefinedBrands.length > 0 && (
                      <Button 
                        variant="outline"
                        onClick={() => addPredefinedBrand(predefinedBrands[0])}
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                      >
                        Quick Add: {predefinedBrands[0].name}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {brands.map((brand) => (
                <Card key={brand.id} className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-blue-500 overflow-hidden" data-testid={`brand-card-${brand.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center shrink-0">
                        <span className="text-xl">🏷️</span>
                      </div>
                      <CardTitle className="text-lg font-bold text-gray-900 truncate flex-1 min-w-0">
                        {brand.name}
                      </CardTitle>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge variant="default" className="bg-green-100 text-green-800 text-[10px] px-1.5">
                        Active
                      </Badge>
                      {canManageBrands && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => startEditBrand(brand)}
                            data-testid={`edit-brand-${brand.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </Button>
                          <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-brand-${brand.id}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </Button>
                          </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Brand</AlertDialogTitle>
                                <AlertDialogDescription asChild>
                                  <div>
                                    Are you sure you want to delete "{brand.name}" brand? This action cannot be undone.
                                    <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                                      <span className="text-sm text-amber-800">
                                        Note: Brands with existing products cannot be deleted.
                                      </span>
                                    </div>
                                  </div>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteBrand(brand.id, brand.name)}
                                  className="bg-red-600 hover:bg-red-700"
                                  data-testid={`confirm-delete-brand-${brand.id}`}
                                >
                                  Delete Brand
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          </>
                        )}
                      </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-3">
                    {brand.description && (
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {brand.description}
                      </p>
                    )}
                    
                    <div className="pt-3 border-t border-gray-100 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Created:</span>
                        <span className="text-gray-900 font-medium">
                          {formatDate(brand.created_at)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Brand ID:</span>
                        <span className="text-gray-900 font-mono text-xs">
                          {brand.id.slice(0, 8)}...
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Brand Dialog */}
      <Dialog open={!!editingBrand} onOpenChange={v => { if (!v) setEditingBrand(null); }}>
        <DialogContent className="max-w-lg z-50">
          <DialogHeader>
            <DialogTitle>Edit Brand</DialogTitle>
            <DialogDescription>Update brand name and description</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Brand Name *</Label>
              <Input value={editBrandForm.name} onChange={e => setEditBrandForm(p => ({ ...p, name: e.target.value }))} placeholder="Brand name" data-testid="edit-brand-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={editBrandForm.description} onChange={e => setEditBrandForm(p => ({ ...p, description: e.target.value }))} placeholder="Brand description" data-testid="edit-brand-description-input" />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button variant="outline" onClick={() => setEditingBrand(null)}>Cancel</Button>
              <Button onClick={saveEditBrand} disabled={!editBrandForm.name.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="save-edit-brand">Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandManagement;